import { Address } from '@/models/address/address'

import { AbstractModel } from '../base'

import type {
  ICoordinates,
  ICoordinatesData,
  IGeoJsonPoint,
  ISite,
  ISiteData,
  ISiteGeofence,
  ISiteLocation,
  ISiteLocationData
} from './site.types'
import type { IAddress } from '@/models/address/address.types'
import { GeofenceShapeType } from './site.constants'

type SiteLike = Partial<ISite> | Partial<ISiteData>

const SITE_UPDATE_OMIT_KEYS: (keyof ISiteData | 'coordinates' | 'geofences')[] = [
  'location',
  'location_source',
  'geocode_status',
  'geocoded_at',
  'coordinates',
  'geofences'
]

/**
 * Derive lat/lng from GeoJSON Point for UI (map center, etc.).
 * GeoJSON order is [longitude, latitude].
 */
export function locationToLatLng(
  location: IGeoJsonPoint | null | undefined
): ICoordinatesData | null {
  if (!location?.coordinates || location.type !== 'Point') return null
  const [lng, lat] = location.coordinates
  if (lat == null || lng == null) return null
  return { latitude: lat, longitude: lng }
}

/**
 * Build create payload from domain or DTO. Sends only name and address (ISiteData subset).
 */
export function toSiteCreatePayload(site: SiteLike): Pick<ISiteData, 'name' | 'address'> {
  return { name: site.name, address: site.address }
}

/**
 * Build update payload from domain or DTO diff. Strips backend-owned location/geocode fields.
 */
export function toSiteUpdatePayload(site: SiteLike): Partial<ISiteData> {
  const result = { ...site } as Record<string, unknown>
  for (const key of SITE_UPDATE_OMIT_KEYS) {
    delete result[key]
  }
  return result as Partial<ISiteData>
}

/**
 * A class representing a Site
 */
export class Site extends AbstractModel implements ISite {
  readonly code?: number
  readonly name: string = ''
  readonly address: IAddress
  readonly geofences: ISiteGeofence[] = []
  readonly location: IGeoJsonPoint | null = null
  readonly location_source?: ISiteData['location_source']
  readonly geocode_status?: ISiteData['geocode_status']
  readonly geocoded_at?: string | null = null

  /** Derived from location for UI (GeoJSON: coordinates[0]=lng, coordinates[1]=lat). */
  get coordinates(): ICoordinates | null {
    const fromLocation = locationToLatLng(this.location)
    if (fromLocation?.latitude != null && fromLocation?.longitude != null) {
      return new Coordinates(fromLocation as ICoordinatesData)
    }
    return null
  }

  constructor(data: ISiteData) {
    super()

    // Validate data
    const requiredFields: (keyof ISiteData)[] = ['address']
    this.validate(data, requiredFields)

    // Initialize object assuming data valid
    if (data.code) {
      this.code = data.code
    }
    if (data.name) {
      this.name = data.name
    }

    this.address = new Address(data.address!)

    if (data.location?.type === 'Point' && Array.isArray(data.location.coordinates)) {
      Object.assign(this, { location: data.location as IGeoJsonPoint })
    }
    if (data.location_source !== undefined) {
      Object.assign(this, { location_source: data.location_source })
    }
    if (data.geocode_status !== undefined) {
      Object.assign(this, { geocode_status: data.geocode_status })
    }
    if (data.geocoded_at !== undefined) {
      Object.assign(this, { geocoded_at: data.geocoded_at })
    }
  }

  toString() {
    if (this.name) {
      return this.name
    }
    return `${this.address.address1}, ${this.address.city}`
  }
}

export class SiteGeofence extends AbstractModel implements ISiteGeofence {
  readonly id?: number
  readonly shape?: GeofenceShapeType | null = null
  readonly center_coordinates?: ICoordinates | null = null
  readonly radius?: number | null = null
  readonly width?: number | null = null
  readonly height?: number | null = null

  constructor(data: ISiteGeofence) {
    super()

    if (data.id) {
      this.id = data.id
    }

    if (data.shape) {
      this.shape = data.shape
    }
    if (data.center_coordinates) {
      this.center_coordinates = new Coordinates(data.center_coordinates)
    }
    if (data.radius) {
      this.radius = data.radius
    }
    if (data.width) {
      this.width = data.width
    }
    if (data.height) {
      this.height = data.height
    }
  }
}

export class Coordinates extends AbstractModel implements ICoordinates {
  readonly latitude: number
  readonly longitude: number

  constructor(data: ICoordinatesData) {
    super()

    const requiredFields: (keyof ICoordinatesData)[] = ['latitude', 'longitude']
    this.validate(data, requiredFields)

    this.latitude = data.latitude!
    this.longitude = data.longitude!
  }
}

export class SiteLocation extends AbstractModel implements ISiteLocation {
  readonly id?: number
  readonly name: string

  constructor(data: ISiteLocationData) {
    super()

    // Validate data
    const requiredFields: (keyof ISiteLocationData)[] = ['name']
    this.validate(data, requiredFields)

    // Initialize object assuming data valid
    this.name = data.name!
    if (data.id) {
      this.id = data.id
    }
  }
}
